import React, { FC, useState } from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useGenericEventsLogger } from '@wix/data-tools-generic-events';
import { useSettings } from '@wix/tpa-settings/react';
import { Button, ButtonPriority, PaddingModes } from 'wix-ui-tpa/cssVars';
import { classes } from './SearchBar.st.css';
import { DateRangePicker } from './DateRangePicker';
import { LocationDropdown } from './LocationDropdown';
import { Search } from '@wix/wix-ui-icons-common';
import { RentalsListDataHooks } from '../dataHooks';
import settingsParams from '../../settingsParams';
import { isSingleDay } from '../../../../utils/dateRange';
import type { FilterBarState, LocationOption } from '../../../../types/types';

type DateRange = FilterBarState['dateRange'];

export type SearchBarProps = {
  filterState: FilterBarState;
  locations: LocationOption[];
  onSearch: (next: FilterBarState) => void;
  disabled?: boolean;
  popoverLayer: HTMLElement | null;
};

const ALL_LOCATIONS = '';

export const SearchBar: FC<SearchBarProps> = ({
  filterState,
  locations,
  onSearch,
  disabled,
  popoverLayer,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const logger = useGenericEventsLogger();
  const settings = useSettings();
  const showLocation = settings.get(settingsParams.showSearchLocation);
  const showTime = settings.get(settingsParams.showSearchTime);
  const [draftLocationId, setDraftLocationId] = useState(
    filterState.locationIds[0] ?? ALL_LOCATIONS,
  );
  const [draftDateRange, setDraftDateRange] = useState(filterState.dateRange);

  const commit = (overrides?: {
    locationId?: string;
    dateRange?: DateRange;
  }) => {
    const locationId = overrides?.locationId ?? draftLocationId;
    const dateRange =
      overrides && 'dateRange' in overrides
        ? overrides.dateRange ?? null
        : draftDateRange;

    onSearch({
      ...filterState,
      locationIds: locationId ? [locationId] : [],
      dateRange,
      timeRange: isSingleDay(dateRange) ? filterState.timeRange : null,
    });
  };

  const handleLocationChange = (locationId: string) => {
    logger.userInput({
      componentName: 'location_filter',
      inputNewValue: locationId,
      inputPrevValue: draftLocationId,
    });
    setDraftLocationId(locationId);

    if (isMobile) {
      commit({ locationId });
    }
  };

  const handleDateApply = (dateRange: DateRange) => {
    setDraftDateRange(dateRange);

    if (isMobile) {
      commit({ dateRange });
    }
  };

  if (!showLocation && !showTime) {
    return null;
  }

  return (
    <div
      className={classes.root}
      role="search"
      data-hook={RentalsListDataHooks.searchBar}
    >
      <div className={classes.fields}>
        {showLocation ? (
          <div className={classes.field}>
            <LocationDropdown
              value={draftLocationId}
              onChange={handleLocationChange}
              locations={locations}
              disabled={disabled}
            />
          </div>
        ) : null}

        {showTime ? (
          <div className={classes.field}>
            <DateRangePicker
              value={draftDateRange}
              onApply={handleDateApply}
              isMobile={isMobile}
              layer={popoverLayer}
            />
          </div>
        ) : null}
      </div>

      {isMobile ? null : (
        <Button
          priority={ButtonPriority.primary}
          paddingMode={PaddingModes.explicitPaddings}
          className={classes.submit}
          disabled={disabled}
          aria-label={t('widget.servicesList.searchBar.searchAriaLabel')}
          data-hook={RentalsListDataHooks.searchSubmit}
          onClick={() => {
            logger.clickCta({ componentName: 'search_button' });
            commit();
          }}
        >
          <Search />
        </Button>
      )}
    </div>
  );
};
